import { FirebaseApp } from 'firebase/app';
import { assign, MachineConfig } from 'xstate';
import { getDatabase } from 'firebase/database';
import { getPriorityInfo as _getPriorityInfo } from '../../services/buildfire/rdb/cases';
import { assignResult } from '../util/actionHelpers';

export enum CaseType {
  PRIORITY = 'priority',
  STANDARD = 'standard',
}

export interface CaseTypeContext {
  firebaseApp: FirebaseApp;
  userId: string;
  caseType: CaseType;
  priorityAvailable: boolean;
  hasLoadedPriorityInfo: boolean;
  error?: string | Error;
  businessStart?: string;
  businessEnd?: string;
  priorityFeePrice?: number; // in cents
}

export type CaseTypeEvent =
  | { type: 'SET_CASE_TYPE'; caseType: CaseType }
  | { type: 'RETRY' }
  | { type: 'NEXT' };

export async function getPriorityInfo(context: CaseTypeContext) {
  try {
    const db = getDatabase(context.firebaseApp);
    const priorityInfo = await _getPriorityInfo(db);

    return {
      ...context,
      businessStart: priorityInfo.businessStart,
      businessEnd: priorityInfo.businessEnd,
      priorityAvailable: priorityInfo.enabled,
      hasLoadedPriorityInfo: true,
      priorityFeePrice: priorityInfo.price,
    };
  } catch (e: any) {
    console.error(e);
    throw e;
  }
}

const caseTypeStates: MachineConfig<CaseTypeContext, any, CaseTypeEvent> = {
  initial: 'initial',
  context: {
    firebaseApp: null as any,
    userId: '',
    caseType: CaseType.STANDARD,
    priorityAvailable: false,
    hasLoadedPriorityInfo: false,
  },

  states: {
    // Only get invoices if we don't already have them loaded
    initial: {
      always: [
        {
          target: 'selectCaseType',
          cond: 'hasLoadedPriorityInfo',
        },
        {
          target: 'load',
        },
      ],
    },
    load: {
      invoke: {
        src: getPriorityInfo,
        onDone: {
          target: 'selectCaseType',
          actions: assignResult,
        },
        onError: {
          target: 'error',
          actions: assign({
            error: (context, ev) => ev.data,
          }),
        },
      },
    },

    selectCaseType: {
      on: {
        SET_CASE_TYPE: {
          actions: 'setCaseType',
        },
      },
    },

    skip: {
      type: 'final',
    },

    error: {
      on: {
        RETRY: {
          target: 'load',
        },
      },
    },
  },
};

export default caseTypeStates;
